import React, { useState, useEffect, useCallback } from 'react';
import styles from '../styles/interactive-docs.module.css';
import {
    DrawPolygonIcon,
    BuildingIcon,
    DrawLineIcon,
    PinIcon,
    LayersIcon,
    RulerIcon,
    CrosshairIcon,
    SettingsIcon,
    ListIcon,
    ExportIcon,
    InfoIcon,
    CheckCircleIcon
} from '../../../components/Icon';

// Полная база знаний по горячим клавишам IChis SPD
export const KEYBOARD_ACTIONS = {
    // Режимы
    'Digit1': {
        code: 'Digit1',
        keyEn: '1',
        keyRu: '1',
        title: 'Режим «Рисование»',
        category: 'modes',
        categoryLabel: 'Режим работы',
        badgeColor: '#f59e0b',
        icon: '✏️',
        summary: 'Переключает рабочее пространство в режим рисования объектов.',
        details: 'Активирует режим «Рисование» в верхней панели. Позволяет наносить земельные участки, здания, линии и метки.',
        tips: ['Очищает предыдущий активный инструмент', 'Клавиши Q, W, E, R, T автоматически включают этот режим']
    },
    'Digit2': {
        code: 'Digit2',
        keyEn: '2',
        keyRu: '2',
        title: 'Режим «Данные»',
        category: 'modes',
        categoryLabel: 'Режим работы',
        badgeColor: '#f59e0b',
        icon: '📊',
        summary: 'Переключает рабочее пространство в режим измерений и работы с реестрами.',
        details: 'Открывает доступ к инструментам замера расстояний, вычисления площадей, пипетке координат и реестру объектов.',
        tips: ['Очищает предыдущий активный инструмент', 'Клавиши A, S, D автоматически переводят систему в этот режим']
    },
    'Digit3': {
        code: 'Digit3',
        keyEn: '3',
        keyRu: '3',
        title: 'Режим «Расширение»',
        category: 'modes',
        categoryLabel: 'Режим работы',
        badgeColor: '#f59e0b',
        icon: '🧩',
        summary: 'Переключает рабочее пространство в режим модулей расширения.',
        details: 'Открывает доступ к дополнительным инструментам расширения системы, экспорту и печати макетов.',
        tips: ['Позволяет работать с подключаемыми модулями расширения карты']
    },

    // Ряд QWERT - Рисование
    'KeyQ': {
        code: 'KeyQ',
        keyEn: 'Q',
        keyRu: 'Й',
        title: 'Инструмент «Участки»',
        category: 'drawing',
        categoryLabel: 'Рисование (QWERT)',
        badgeColor: '#10b981',
        icon: <DrawPolygonIcon size={20} />,
        summary: 'Нанесение границ земельных участков (многоугольники/полигоны).',
        details: 'Активирует инструмент рисования участков. Автоматически переключает режим в «Рисование». Кликайте по карте для расстановки вершин.',
        tips: [
            'Удерживайте Shift для ортогональной привязки (углы 90° и 45°)',
            'Нажмите Backspace или Ctrl+Z для отмены последней точки',
            'Двойной клик или Enter — замкнуть контур и открыть Паспорт участка'
        ]
    },
    'KeyW': {
        code: 'KeyW',
        keyEn: 'W',
        keyRu: 'Ц',
        title: 'Инструмент «Здания»',
        category: 'drawing',
        categoryLabel: 'Рисование (QWERT)',
        badgeColor: '#10b981',
        icon: <BuildingIcon size={20} />,
        summary: 'Нанесение капитальных строений и контуров зданий.',
        details: 'Позволяет очертить контур строения. В Паспорте созданного объекта можно задать высоту в метрах и материал отделки.',
        tips: [
            'Удерживайте Shift для ровных прямоугольных контуров',
            'Двойной клик или Enter фиксирует контур здания'
        ]
    },
    'KeyE': {
        code: 'KeyE',
        keyEn: 'E',
        keyRu: 'У',
        title: 'Инструмент «Линии»',
        category: 'drawing',
        categoryLabel: 'Рисование (QWERT)',
        badgeColor: '#10b981',
        icon: <DrawLineIcon size={20} />,
        summary: 'Трассировка линейных объектов (дороги, проезды, заборы, инженерные сети).',
        details: 'Создает непрерывные линии и трассы. В Паспорте можно выбрать тип начертания (сплошная, пунктирная, штрихпунктирная), цвет и толщину.',
        tips: [
            'Enter или двойной клик завершает прокладку линии',
            'Система автоматически подсчитывает общую длину трассы в метрах'
        ]
    },
    'KeyR': {
        code: 'KeyR',
        keyEn: 'R',
        keyRu: 'К',
        title: 'Инструмент «Метки»',
        category: 'drawing',
        categoryLabel: 'Рисование (QWERT)',
        badgeColor: '#10b981',
        icon: <PinIcon size={20} />,
        summary: 'Установка точечных меток и ориентиров на карте.',
        details: 'Клик по карте ставит метку. В Паспорте объекта можно выбрать категорию, название и подходящую иконку.',
        tips: [
            'Категории и значки настраиваются в Легенде',
            'Метки отображаются на интерактивной карте'
        ]
    },
    'KeyT': {
        code: 'KeyT',
        keyEn: 'T',
        keyRu: 'Е',
        title: 'Инструмент «Подложка»',
        category: 'drawing',
        categoryLabel: 'Рисование (QWERT)',
        badgeColor: '#10b981',
        icon: <LayersIcon size={20} />,
        summary: 'Управление растровыми чертежами и мастер-планами генплана.',
        details: 'Открывает панель загрузки и геопривязки растровых изображений (PNG/JPG/PDF) по 4 угловым опорным точкам.',
        tips: [
            'Позволяет настроить прозрачность подложки от 0% до 100%',
            'Чертеж точно накладывается на спутниковые снимки'
        ]
    },

    // Ряд ASDFG - Данные и измерения
    'KeyA': {
        code: 'KeyA',
        keyEn: 'A',
        keyRu: 'Ф',
        title: 'Инструмент «Линейка»',
        category: 'data',
        categoryLabel: 'Данные и измерения (ASDFG)',
        badgeColor: '#2563eb',
        icon: <RulerIcon size={20} />,
        summary: 'Геодезическое измерение расстояний и протяженности маршрута.',
        details: 'Вычисляет точную длину ломаной линии по сфере Земли с учетом кривизны. Отображает общую дистанцию и отрезки.',
        tips: [
            'Кликайте по точкам для построения маршрута',
            'Двойной клик фиксирует замер, Esc очищает'
        ]
    },
    'KeyS': {
        code: 'KeyS',
        keyEn: 'S',
        keyRu: 'Ы',
        title: 'Инструмент «Площадь»',
        category: 'data',
        categoryLabel: 'Данные и измерения (ASDFG)',
        badgeColor: '#2563eb',
        icon: <DrawPolygonIcon size={20} />,
        summary: 'Мгновенный расчет площади произвольного контура на карте.',
        details: 'Позволяет измерить площадь любой территории. Результат выводится одновременно в квадратных метрах (м²), сотках и гектарах (га).',
        tips: [
            'Удобно для быстрой оценки пятна застройки или незанятых зон'
        ]
    },
    'KeyD': {
        code: 'KeyD',
        keyEn: 'D',
        keyRu: 'В',
        title: 'Инструмент «Координаты»',
        category: 'data',
        categoryLabel: 'Данные и измерения (ASDFG)',
        badgeColor: '#2563eb',
        icon: <CrosshairIcon size={20} />,
        summary: 'Пипетка координат и пространственный определитель объекта.',
        details: 'Клик в любую точку карты выводит точные координаты WGS-84 (широта, долгота) и находит объект под курсором.',
        tips: [
            'Клик по найденному объекту сразу открывает его свойства для редактирования'
        ]
    },
    'KeyF': {
        code: 'KeyF',
        keyEn: 'F',
        keyRu: 'А',
        title: 'Менеджер объектов',
        category: 'data',
        categoryLabel: 'Данные и измерения (ASDFG)',
        badgeColor: '#2563eb',
        icon: <SettingsIcon size={20} />,
        summary: 'Генеральный реестр и таблица всех объектов карты.',
        details: 'Открывает боковую панель со списком всех участков, строений, линий и меток с поиском, фильтрами, сортировкой и инспектором дубликатов.',
        tips: [
            'Содержит встроенный «Инспектор дубликатов» для поиска наложений',
            'Позволяет массово скрывать, удалять или экспортировать объекты'
        ]
    },
    'KeyG': {
        code: 'KeyG',
        keyEn: 'G',
        keyRu: 'П',
        title: 'Легенда категорий',
        category: 'data',
        categoryLabel: 'Данные и измерения (ASDFG)',
        badgeColor: '#2563eb',
        icon: <ListIcon size={20} />,
        summary: 'Управление категориями, цветами, штриховками и значками.',
        details: 'Открывает окно настройки каталога: создание новых статусов, назначение цветов палитры, выбор штриховок и типов линий.',
        tips: [
            'Изменения в легенде моментально применяются ко всем объектам на карте'
        ]
    },

    // Ряд ZXCV - Действия
    'KeyZ': {
        code: 'KeyZ',
        keyEn: 'Z',
        keyRu: 'Я',
        title: 'Отмена действия (Undo)',
        category: 'actions',
        categoryLabel: 'Быстрые действия (ZXCV)',
        badgeColor: '#8b5cf6',
        icon: '↶',
        summary: 'Откат последней точки, правки вершины или топологии.',
        details: 'Универсальная отмена: при рисовании удаляет последнюю точку; при правке границ возвращает вершину назад; при работе с топологией откатывает вычитание/слияние.',
        tips: [
            'Работает как по одиночной клавише Z/Я, так и через сочетание Ctrl + Z'
        ]
    },
    'KeyX': {
        code: 'KeyX',
        keyEn: 'X',
        keyRu: 'Ч',
        title: 'Сброс / Режим выбора',
        category: 'actions',
        categoryLabel: 'Быстрые действия (ZXCV)',
        badgeColor: '#8b5cf6',
        icon: '✖',
        summary: 'Деактивация текущего инструмента и возврат к курсору выбора.',
        details: 'Сбрасывает любой активный инструмент (линейку, площадь, рисование) и переводит карту в режим стандартного выбора и клика по объектам.',
        tips: [
            'Быстрый способ прекратить замер или снять выделение'
        ]
    },
    'KeyC': {
        code: 'KeyC',
        keyEn: 'C',
        keyRu: 'С',
        title: 'Повтор действия (Redo)',
        category: 'actions',
        categoryLabel: 'Быстрые действия (ZXCV)',
        badgeColor: '#8b5cf6',
        icon: '↷',
        summary: 'Повтор ранее отмененного действия (вершины, история).',
        details: 'Возвращает отмененную команду. Работает при редактировании контура объекта и в операциях топологии модуля SPD+.',
        tips: [
            'Работает по клавише C/С, а также по комбинациям Ctrl + Y и Ctrl + Shift + Z'
        ]
    },
    'KeyV': {
        code: 'KeyV',
        keyEn: 'V',
        keyRu: 'М',
        title: 'Экспорт данных',
        category: 'actions',
        categoryLabel: 'Быстрые действия (ZXCV)',
        badgeColor: '#8b5cf6',
        icon: <ExportIcon size={20} />,
        summary: 'Мгновенная выгрузка базы геоданных в формате GeoJSON.',
        details: 'Формирует и скачивает файл `map_data.json`. Если на карте выбраны объекты через Shift, экспортирует только выбранные.',
        tips: [
            'GeoJSON полностью совместим с QGIS, AutoCAD, MapLibre и ArcGIS'
        ]
    },

    // Системные и вспомогательные
    'Escape': {
        code: 'Escape',
        keyEn: 'Esc',
        keyRu: 'Esc',
        title: 'Клавиша Escape (Отмена)',
        category: 'system',
        categoryLabel: 'Системные клавиши',
        badgeColor: '#64748b',
        icon: '⎋',
        summary: 'Закрытие модальных окон, паспорта объекта и выход из рисования.',
        details: 'Универсальная клавиша отмены: закрывает открытые модальные панели, сбрасывает незавершенный контур, отменяет редактирование геометрии.',
        tips: ['При редактировании геометрии восстанавливает исходный контур']
    },
    'Enter': {
        code: 'Enter',
        keyEn: 'Enter',
        keyRu: 'Enter',
        title: 'Клавиша Enter (Подтверждение)',
        category: 'system',
        categoryLabel: 'Системные клавиши',
        badgeColor: '#64748b',
        icon: '↵',
        summary: 'Завершение рисования или сохранение отредактированной геометрии.',
        details: 'Замыкает полигон участка/здания или фиксирует трассу линии, после чего автоматически открывает Паспорт созданного объекта.',
        tips: ['Альтернатива: двойной клик мыши на последней точке']
    },
    'Backspace': {
        code: 'Backspace',
        keyEn: 'Backspace',
        keyRu: '⌫',
        title: 'Клавиша Backspace / Delete',
        category: 'system',
        categoryLabel: 'Системные клавиши',
        badgeColor: '#64748b',
        icon: '⌫',
        summary: 'Удаление последней точки или выделенной вершины.',
        details: 'При рисовании удаляет последний поставленный узел. При редактировании геометрии удаляет выбранную узловую точку контура.',
        tips: ['Позволяет легко исправить случайно поставленную точку без перезапуска']
    },
    'ShiftLeft': {
        code: 'ShiftLeft',
        keyEn: 'Shift',
        keyRu: 'Shift',
        title: 'Клавиша Shift (Ортогональность и Мультивыбор)',
        category: 'system',
        categoryLabel: 'Системные клавиши',
        badgeColor: '#64748b',
        icon: '⇧',
        summary: 'Привязка прямых углов 90°/45° и множественный выбор участков.',
        details: 'При рисовании: удерживайте Shift для строго прямоугольных углов (система автоматически достраивает 4-й угол прямоугольника). При кликах по объектам: позволяет выделить несколько участков одновременно.',
        tips: ['После выделения объектов через Shift при отпускании открывается Менеджер объектов']
    }
};

// Физическая сетка клавиатуры
const KEYBOARD_ROWS = [
    // Ряд 1: Цифры
    [
        { code: 'Escape', label: 'Esc', width: 'keySmall' },
        { code: 'Digit1', label: '1', sub: '!' },
        { code: 'Digit2', label: '2', sub: '@' },
        { code: 'Digit3', label: '3', sub: '#' },
        { code: 'Digit4', label: '4', sub: '$' },
        { code: 'Digit5', label: '5', sub: '%' },
        { code: 'Digit6', label: '6', sub: '^' },
        { code: 'Digit7', label: '7', sub: '&' },
        { code: 'Digit8', label: '8', sub: '*' },
        { code: 'Digit9', label: '9', sub: '(' },
        { code: 'Digit0', label: '0', sub: ')' },
        { code: 'Minus', label: '-', sub: '_' },
        { code: 'Equal', label: '=', sub: '+' },
        { code: 'Backspace', label: 'Backspace', width: 'keyWide' }
    ],
    // Ряд 2: QWERT
    [
        { code: 'Tab', label: 'Tab', width: 'keyMed' },
        { code: 'KeyQ', label: 'Q', sub: 'Й' },
        { code: 'KeyW', label: 'W', sub: 'Ц' },
        { code: 'KeyE', label: 'E', sub: 'У' },
        { code: 'KeyR', label: 'R', sub: 'К' },
        { code: 'KeyT', label: 'T', sub: 'Е' },
        { code: 'KeyY', label: 'Y', sub: 'Н' },
        { code: 'KeyU', label: 'U', sub: 'Г' },
        { code: 'KeyI', label: 'I', sub: 'Ш' },
        { code: 'KeyO', label: 'O', sub: 'Щ' },
        { code: 'KeyP', label: 'P', sub: 'З' },
        { code: 'BracketLeft', label: '[', sub: 'Х' },
        { code: 'BracketRight', label: ']', sub: 'Ъ' },
        { code: 'Backslash', label: '\\', sub: '|', width: 'keyMed' }
    ],
    // Ряд 3: ASDFG
    [
        { code: 'CapsLock', label: 'Caps', width: 'keyMedPlus' },
        { code: 'KeyA', label: 'A', sub: 'Ф' },
        { code: 'KeyS', label: 'S', sub: 'Ы' },
        { code: 'KeyD', label: 'D', sub: 'В' },
        { code: 'KeyF', label: 'F', sub: 'А' },
        { code: 'KeyG', label: 'G', sub: 'П' },
        { code: 'KeyH', label: 'H', sub: 'Р' },
        { code: 'KeyJ', label: 'J', sub: 'О' },
        { code: 'KeyK', label: 'K', sub: 'Л' },
        { code: 'KeyL', label: 'L', sub: 'Д' },
        { code: 'Semicolon', label: ';', sub: 'Ж' },
        { code: 'Quote', label: "'", sub: 'Э' },
        { code: 'Enter', label: 'Enter', width: 'keyEnter' }
    ],
    // Ряд 4: ZXCV
    [
        { code: 'ShiftLeft', label: 'Shift', width: 'keyShift' },
        { code: 'KeyZ', label: 'Z', sub: 'Я' },
        { code: 'KeyX', label: 'X', sub: 'Ч' },
        { code: 'KeyC', label: 'C', sub: 'С' },
        { code: 'KeyV', label: 'V', sub: 'М' },
        { code: 'KeyB', label: 'B', sub: 'И' },
        { code: 'KeyN', label: 'N', sub: 'Т' },
        { code: 'KeyM', label: 'M', sub: 'Ь' },
        { code: 'Comma', label: ',', sub: 'Б' },
        { code: 'Period', label: '.', sub: 'Ю' },
        { code: 'Slash', label: '/', sub: '?' },
        { code: 'ShiftRight', label: 'Shift', width: 'keyShift' }
    ],
    // Ряд 5: Пробел и модификаторы
    [
        { code: 'ControlLeft', label: 'Ctrl', width: 'keyMed' },
        { code: 'AltLeft', label: 'Alt', width: 'keyMed' },
        { code: 'Space', label: 'Пробел (Свободный обзор и панорама карты)', width: 'keySpace' },
        { code: 'AltRight', label: 'Alt', width: 'keyMed' },
        { code: 'ControlRight', label: 'Ctrl', width: 'keyMed' }
    ]
];

const CATEGORY_FILTERS = [
    { id: 'all', label: 'Все клавиши' },
    { id: 'drawing', label: '✏️ Рисование (Q-W-E-R-T)', color: '#10b981' },
    { id: 'data', label: '📊 Данные (A-S-D-F-G)', color: '#2563eb' },
    { id: 'actions', label: '⚡ Действия (Z-X-C-V)', color: '#8b5cf6' },
    { id: 'modes', label: '🎛️ Режимы (1, 2, 3)', color: '#f59e0b' },
    { id: 'system', label: '⚙️ Системные (Esc, Enter, Shift)', color: '#64748b' }
];

export const InteractiveKeyboard = () => {
    const [selectedCode, setSelectedCode] = useState('KeyQ');
    const [activeFilter, setActiveFilter] = useState('all');
    const [lastPressedCode, setLastPressedCode] = useState(null);

    // Слушатель физической клавиатуры при просмотре страницы документации
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
            if (isInput) return;

            let targetCode = e.code;
            if (targetCode === 'ShiftRight') targetCode = 'ShiftLeft';

            if (KEYBOARD_ACTIONS[targetCode]) {
                setSelectedCode(targetCode);
                setLastPressedCode(targetCode);
                const timer = setTimeout(() => setLastPressedCode(null), 300);
                return () => clearTimeout(timer);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const selectedAction = KEYBOARD_ACTIONS[selectedCode] || KEYBOARD_ACTIONS['KeyQ'];

    const isKeyHighlighted = useCallback((keyObj) => {
        const code = keyObj.code === 'ShiftRight' ? 'ShiftLeft' : keyObj.code;
        const action = KEYBOARD_ACTIONS[code];
        if (!action) return false;
        if (activeFilter === 'all') return true;
        return action.category === activeFilter;
    }, [activeFilter]);

    const getKeyCategoryClass = (code) => {
        const targetCode = code === 'ShiftRight' ? 'ShiftLeft' : code;
        const action = KEYBOARD_ACTIONS[targetCode];
        if (!action) return '';
        switch (action.category) {
            case 'drawing': return styles.kbKeyDrawing;
            case 'data': return styles.kbKeyData;
            case 'actions': return styles.kbKeyActions;
            case 'modes': return styles.kbKeyModes;
            case 'system': return styles.kbKeySystem;
            default: return '';
        }
    };

    return (
        <div className={styles.kbWidgetRoot}>
            {/* Панель фильтров категорий */}
            <div className={styles.kbFilterBar}>
                <div className={styles.kbFilterLabel}>
                    <span className={styles.kbPulseDot} />
                    <span>Группы:</span>
                </div>
                <div className={styles.kbFilterButtons}>
                    {CATEGORY_FILTERS.map(f => (
                        <button
                            key={f.id}
                            type="button"
                            className={`${styles.kbFilterBtn} ${activeFilter === f.id ? styles.kbFilterBtnActive : ''}`}
                            onClick={() => setActiveFilter(f.id)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className={styles.kbLiveHint}>
                    ⌨️ <span>Нажмите физическую клавишу или кликните мышью</span>
                </div>
            </div>

            {/* Физический виджет клавиатуры */}
            <div className={styles.kbChassis}>
                {KEYBOARD_ROWS.map((row, rIdx) => (
                    <div key={rIdx} className={styles.kbRow}>
                        {row.map((k) => {
                            const codeToLookup = k.code === 'ShiftRight' ? 'ShiftLeft' : k.code;
                            const action = KEYBOARD_ACTIONS[codeToLookup];
                            const isSelected = selectedCode === codeToLookup;
                            const isPressed = lastPressedCode === codeToLookup || lastPressedCode === k.code;
                            const isHighlighted = isKeyHighlighted(k);
                            const categoryClass = getKeyCategoryClass(k.code);
                            const widthClass = k.width ? styles[k.width] : '';

                            return (
                                <button
                                    key={k.code}
                                    type="button"
                                    className={`
                                        ${styles.kbKey}
                                        ${widthClass}
                                        ${categoryClass}
                                        ${isSelected ? styles.kbKeySelected : ''}
                                        ${isPressed ? styles.kbKeyPressed : ''}
                                        ${!isHighlighted && action ? styles.kbKeyDimmed : ''}
                                        ${!action ? styles.kbKeyGeneric : ''}
                                    `}
                                    onClick={() => {
                                        if (action) {
                                            setSelectedCode(codeToLookup);
                                        }
                                    }}
                                    title={action ? `${action.title} (${action.categoryLabel})` : k.label}
                                >
                                    <span className={styles.kbKeyMain}>{k.label}</span>
                                    {k.sub && <span className={styles.kbKeySub}>{k.sub}</span>}
                                    {action && (
                                        <span
                                            className={styles.kbKeyIndicator}
                                            style={{ backgroundColor: action.badgeColor }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Детальная карточка выбранной клавиши */}
            {selectedAction && (
                <div className={styles.kbDetailCard}>
                    <div className={styles.kbDetailHeader}>
                        <div className={styles.kbKeyPreviewBadge}>
                            <span className={styles.kbKeyPreviewEn}>{selectedAction.keyEn}</span>
                            {selectedAction.keyRu && selectedAction.keyRu !== selectedAction.keyEn && (
                                <span className={styles.kbKeyPreviewRu}>{selectedAction.keyRu}</span>
                            )}
                        </div>

                        <div className={styles.kbDetailTitleGroup}>
                            <div className={styles.kbDetailCategoryBadge} style={{ color: selectedAction.badgeColor, borderColor: selectedAction.badgeColor }}>
                                <span>{selectedAction.categoryLabel}</span>
                            </div>
                            <h3 className={styles.kbDetailTitle}>
                                <span className={styles.kbDetailIcon}>{selectedAction.icon}</span>
                                <span>{selectedAction.title}</span>
                            </h3>
                            <p className={styles.kbDetailSummary}>{selectedAction.summary}</p>
                        </div>
                    </div>

                    <div className={styles.kbDetailBody}>
                        <div className={styles.kbDetailSection}>
                            <h4 className={styles.kbDetailSubheader}>Как это работает в редакторе:</h4>
                            <p className={styles.kbDetailText}>{selectedAction.details}</p>
                        </div>

                        {selectedAction.tips && selectedAction.tips.length > 0 && (
                            <div className={styles.kbDetailTipsSection}>
                                <h4 className={styles.kbDetailSubheader}>Полезные комбинации и приемы:</h4>
                                <ul className={styles.kbDetailTipsList}>
                                    {selectedAction.tips.map((tip, tIdx) => (
                                        <li key={tIdx} className={styles.kbDetailTipItem}>
                                            <span className={styles.kbDetailCheck}><CheckCircleIcon size={14} /></span>
                                            <span>{tip}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InteractiveKeyboard;
